import { useCallback, useMemo, useState } from "react";
import { Map, MapMarker } from "react-kakao-maps-sdk";
import styled from "styled-components";
import useKakaoMapsLoader from "../../components/contents/intro/branch/useKakaoMapsLoader";
import { branchcontext } from "../../context/intro/branch/branchcontext";

const DEFAULT_CENTER = { lat: 37.5665, lng: 126.978 };

const toDMS = (deg, isLat) => {
  const abs = Math.abs(deg);
  const d = Math.floor(abs);
  const mFloat = (abs - d) * 60;
  const m = Math.floor(mFloat);
  const s = ((mFloat - m) * 60).toFixed(1);
  const hemi = isLat ? (deg >= 0 ? "N" : "S") : deg >= 0 ? "E" : "W";
  return `${d}°${String(m).padStart(2, "0")}'${s}"${hemi}`;
};

const copy = (text) => navigator.clipboard?.writeText(text);

function CoordFinderPage() {
  const { isLoaded } = useKakaoMapsLoader();
  const [query, setQuery] = useState("");
  const [center, setCenter] = useState(DEFAULT_CENTER);
  const [pin, setPin] = useState(null);
  const [matchedAddress, setMatchedAddress] = useState("");
  const [error, setError] = useState("");

  const branchPresets = useMemo(
    () =>
      branchcontext
        .filter((b) => b.address1 && b.addressKey)
        .map((b) => ({
          label: `${b.location} — ${b.address1} ${b.addressKey}`,
          query: `${b.address1} ${b.addressKey}`,
          coordinate: b.coordinate,
        })),
    []
  );

  const search = useCallback((address) => {
    setError("");
    if (!address?.trim()) return;
    if (!window.kakao?.maps?.services) {
      setError("Kakao services 라이브러리가 아직 준비되지 않았습니다.");
      return;
    }
    const geocoder = new window.kakao.maps.services.Geocoder();
    geocoder.addressSearch(address, (result, status) => {
      if (status === window.kakao.maps.services.Status.OK && result[0]) {
        const { x, y, address_name } = result[0];
        const next = { lat: parseFloat(y), lng: parseFloat(x) };
        setPin(next);
        setCenter(next);
        setMatchedAddress(address_name);
      } else {
        setError(`조회 실패: ${address}`);
        setPin(null);
        setMatchedAddress("");
      }
    });
  }, []);

  const reverseGeocode = useCallback((lat, lng) => {
    if (!window.kakao?.maps?.services) return;
    const geocoder = new window.kakao.maps.services.Geocoder();
    geocoder.coord2Address(lng, lat, (result, status) => {
      if (status === window.kakao.maps.services.Status.OK && result[0]) {
        const road = result[0].road_address?.address_name;
        const jibun = result[0].address?.address_name;
        setMatchedAddress(road || jibun || "");
      }
    });
  }, []);

  const onMapClick = useCallback(
    (_, mouseEvent) => {
      const latLng = mouseEvent.latLng;
      const next = { lat: latLng.getLat(), lng: latLng.getLng() };
      setPin(next);
      reverseGeocode(next.lat, next.lng);
    },
    [reverseGeocode]
  );

  const onMarkerDragEnd = useCallback(
    (marker) => {
      const pos = marker.getPosition();
      const next = { lat: pos.getLat(), lng: pos.getLng() };
      setPin(next);
      reverseGeocode(next.lat, next.lng);
    },
    [reverseGeocode]
  );

  if (!isLoaded) return <div>지도 로딩 중...</div>;

  const decimal = pin ? `${pin.lat.toFixed(6)}, ${pin.lng.toFixed(6)}` : "";
  const dms = pin ? `${toDMS(pin.lat, true)} ${toDMS(pin.lng, false)}` : "";
  const jsObject = pin
    ? `{ lat: ${pin.lat.toFixed(6)}, lng: ${pin.lng.toFixed(6)} }`
    : "";

  return (
    <Wrap>
      <h2>좌표 조회 (Kakao Geocoder)</h2>
      <Section>
        <Row>
          <Input
            placeholder="주소 입력 (도로명/지번)"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && search(query)}
          />
          <Button onClick={() => search(query)}>조회</Button>
        </Row>
        <Hint>지도 클릭 또는 마커 드래그로 위치 미세조정 가능</Hint>
      </Section>

      <Section>
        <SubTitle>지점 프리셋</SubTitle>
        <PresetList>
          {branchPresets.map((p) => (
            <PresetButton
              key={p.label}
              onClick={() => {
                setQuery(p.query);
                search(p.query);
              }}
            >
              {p.label}
            </PresetButton>
          ))}
        </PresetList>
      </Section>

      <MapWrap>
        <Map
          center={center}
          style={{ width: "100%", height: "100%" }}
          level={3}
          onClick={onMapClick}
        >
          {pin && (
            <MapMarker
              position={pin}
              draggable
              onDragEnd={onMarkerDragEnd}
            />
          )}
        </Map>
      </MapWrap>

      {error && <ErrorBox>{error}</ErrorBox>}

      {pin && (
        <Result>
          <ResultRow>
            <Label>매칭 주소</Label>
            <Value>{matchedAddress || "-"}</Value>
          </ResultRow>
          <ResultRow>
            <Label>십진수</Label>
            <Value>{decimal}</Value>
            <CopyButton onClick={() => copy(decimal)}>복사</CopyButton>
          </ResultRow>
          <ResultRow>
            <Label>DMS</Label>
            <Value>{dms}</Value>
            <CopyButton onClick={() => copy(dms)}>복사</CopyButton>
          </ResultRow>
          <ResultRow>
            <Label>JS 객체</Label>
            <Value>{jsObject}</Value>
            <CopyButton onClick={() => copy(jsObject)}>복사</CopyButton>
          </ResultRow>
        </Result>
      )}
    </Wrap>
  );
}

export default CoordFinderPage;

const Wrap = styled.div`
  max-width: 960px;
  margin: 2rem auto;
  padding: 0 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;
const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;
const SubTitle = styled.div`
  font-weight: bold;
`;
const Row = styled.div`
  display: flex;
  gap: 0.5rem;
`;
const Input = styled.input`
  flex: 1;
  padding: 0.6rem 0.8rem;
  border: 1px solid #ccc;
  border-radius: 0.3rem;
  font-size: 1rem;
`;
const Button = styled.button`
  padding: 0.6rem 1.2rem;
  background: #fee500;
  border: none;
  border-radius: 0.3rem;
  cursor: pointer;
  font-weight: bold;
`;
const Hint = styled.div`
  color: #888;
  font-size: 0.85rem;
`;
const PresetList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
`;
const PresetButton = styled.button`
  padding: 0.4rem 0.7rem;
  background: #f4f4f4;
  border: 1px solid #ddd;
  border-radius: 0.3rem;
  cursor: pointer;
  font-size: 0.85rem;
`;
const MapWrap = styled.div`
  width: 100%;
  height: 28rem;
  border: 1px solid #ddd;
  border-radius: 0.3rem;
  overflow: hidden;
`;
const Result = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 1rem;
  background: #fafafa;
  border-radius: 0.3rem;
`;
const ResultRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.7rem;
`;
const Label = styled.div`
  width: 6rem;
  font-weight: bold;
  color: #555;
`;
const Value = styled.div`
  flex: 1;
  font-family: monospace;
  word-break: break-all;
`;
const CopyButton = styled.button`
  padding: 0.3rem 0.7rem;
  background: #fff;
  border: 1px solid #ccc;
  border-radius: 0.3rem;
  cursor: pointer;
  font-size: 0.8rem;
`;
const ErrorBox = styled.div`
  padding: 0.7rem;
  background: #fff0f0;
  color: #c00;
  border-radius: 0.3rem;
`;
